import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { Modal } from "semantic-ui-react";
import { FilledButton, MainModalHeaderWithCustomRight } from "./Elements";

export const AlertModalContext = createContext(null);

export const useAlertModal = () => useContext(AlertModalContext);

/**
 * 글로벌 ref — Router 의 `getUserConfirmation` 처럼 React 트리 밖에서 showAlert 가 필요할 때 사용.
 * Router 가 AlertModalProvider 보다 상위에 있으면 context 로 받을 수 없으므로 이 ref 사용.
 */
export const globalAlertRef = { showAlert: null };

export const AlertModalProvider = ({ children }) => {
  const [modal, setModal] = useState({
    open: false,
    title: "",
    description: "",
    buttons: [],
    resolve: null,
  });

  // Alert 호출 함수
  const showAlert = useCallback(({ title, description, buttons }) => {
    return new Promise((resolve) => {
      setModal({
        open: true,
        title,
        description,
        buttons,
        resolve,
      });
    });
  }, []);

  // Router 의 getUserConfirmation 같이 외부 시스템에서 호출하도록 글로벌 ref 에 등록.
  useEffect(() => {
    globalAlertRef.showAlert = showAlert;
    return () => {
      globalAlertRef.showAlert = null;
    };
  }, [showAlert]);

  const closeModal = (result) => {
    // eslint-disable-next-line no-unused-expressions
    modal.resolve?.(result);
    setModal((m) => ({ ...m, open: false }));
  };

  return (
    <AlertModalContext.Provider value={{ showAlert }}>
      {children}

      <Modal
        open={modal.open}
        onClose={() => closeModal("cancel")}
        closeOnDimmerClick={false}
        style={{ maxWidth: "550px", minWidth: "350px" }}
      >
        <MainModalHeaderWithCustomRight
          title={modal.title}
          onCancel={() => closeModal("cancel")}
          hasBottomDivider
        />
        <Modal.Content scrolling>
          <div className="h6-medium" style={{ whiteSpace: "pre-wrap" }}>
            {modal.description}
          </div>
          {modal.buttons?.length > 0 && (
            <div className="mt-small flex-end" style={{ gap: "15px" }}>
              {modal.buttons.map((btn, idx) => (
                <FilledButton
                  key={idx}
                  buttonColor={btn.color || "coleGray"}
                  customStyle={{ minWidth: "100px" }}
                  onClick={() => {
                    // eslint-disable-next-line no-unused-expressions
                    btn.onClick?.();
                    closeModal(btn.value ?? btn.text);
                  }}
                  label={btn.text}
                />
              ))}
            </div>
          )}
        </Modal.Content>
      </Modal>
    </AlertModalContext.Provider>
  );
};
